import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Popconfirm, Space, Table, Tag, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useDeleteEvent } from '../hooks';
import * as fmt from '../format';
import { eventFlags, lineLitres, skuCatalog } from '../events';

const { Text, Paragraph } = Typography;

const EventsTab = ({ plan, canEdit, onOpenEvent }) => {
  const deleteEvent = useDeleteEvent();
  const catalog = useMemo(() => skuCatalog(plan), [plan]);
  const events = plan.events || [];

  const columns = [
    {
      title: 'When',
      render: (_, event) => (
        event.startDate === event.endDate
          ? fmt.date(event.startDate)
          : `${fmt.shortDate(event.startDate)} – ${fmt.date(event.endDate)}`
      ),
    },
    {
      title: 'Event',
      dataIndex: 'name',
      render: (name, event) => {
        const past = event.endDate < plan.today;
        const flags = eventFlags(event, plan, catalog);
        return (
          <Space size={4} wrap>
            <Text strong={!past} type={past ? 'secondary' : undefined}>{name}</Text>
            {flags.includes('outside') && <Tag>Outside horizon</Tag>}
            {flags.includes('missing') && <Tag color="orange">SKU not in plan</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'SKUs',
      dataIndex: 'lines',
      render: (lines) => (
        <Text type="secondary">
          {(lines || []).map((line) => {
            const litres = lineLitres(line, catalog);
            const qty = line.unit === 'litres' || litres == null
              ? `${fmt.number(line.quantity)} ${line.unit === 'units' ? 'units' : 'L'}`
              : `${fmt.number(line.quantity)} units (${fmt.litres(litres)})`;
            return `${line.sku}: ${qty}`;
          }).join(', ') || '—'}
        </Text>
      ),
    },
    {
      title: 'Litres',
      align: 'right',
      render: (_, event) => {
        const total = (event.lines || []).reduce(
          (sum, line) => sum + (lineLitres(line, catalog) || 0),
          0,
        );
        const known = (event.lines || []).every((line) => lineLitres(line, catalog) != null);
        return known ? fmt.litres(total) : '—';
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      ellipsis: true,
      render: (n) => n || '—',
    },
    {
      title: '',
      width: 150,
      render: (_, event) => canEdit && (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => onOpenEvent(event)}>
            Edit
          </Button>
          <Popconfirm title="Remove this event?" onConfirm={() => deleteEvent.mutate(event.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Paragraph type="secondary" style={{ maxWidth: 820 }}>
        Events adjust the forecast for something the model cannot see yet: a festival, a big
        order, or volume you know will not sell. Litres are spread across the dates and added
        to that SKU&apos;s demand, so stock and brew recommendations update straight away.
        Use the same start and end date for an order due that week.
      </Paragraph>
      <div className="forecasting-toolbar">
        <span />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenEvent(null)}>
            Add event
          </Button>
        )}
      </div>
      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={[...events].sort((a, b) => (
          String(a.startDate).localeCompare(String(b.startDate)) || a.id - b.id
        ))}
        rowClassName={(event) => (event.endDate < plan.today ? 'forecasting-event-past' : '')}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: 'No demand events yet' }}
      />
    </div>
  );
};

EventsTab.propTypes = {
  plan: PropTypes.shape({
    today: PropTypes.string,
    weekStarts: PropTypes.arrayOf(PropTypes.string),
    beers: PropTypes.arrayOf(PropTypes.object),
    events: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onOpenEvent: PropTypes.func.isRequired,
};

export default EventsTab;
